import React, { useState } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { Toast } from '@vzmi/shp_shared_compo'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper from '../../../../../lib/errorHelper'
import { httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { RES_TYPE } from './constants'
import ECouponModal, { DISCOUNT_RULE_TYPES, COUPON_FIELD } from '../CustomerInterest/ECouponModal/ECouponModal'
import { TARGET_AUDIENCE } from './RankingTable/RankingTable'

export const createSupplierCouponsData = async (params, req) => {
  const [reqError, payload = []] = await safeAwait(req(params))
  if (reqError) {
    return [{ error: reqError, params }]
  }
  return [undefined, payload]
}

export const getProductBundlesData = async (listingId, req) => {
  const params = {
    id: listingId
  }

  const [reqError, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseShoppingProductsErrorMessage(reqError)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  // 第一筆是活動
  const promotion = _.get(payload, 'promotions[0]', {})
  const { title, endTs } = promotion
  const result = {
    title,
    promotionEndTs: moment(endTs).format('YYYY/MM/DD HH:mm')
  }
  return [undefined, result]
}

export const getConvertedCreatedPayload = (formValues) => {
  let newDiscountThreshold = _.get(formValues, COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD)
  let newDiscountAmount = _.get(formValues, COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT)
  if (formValues.discountRule === DISCOUNT_RULE_TYPES.MINTOTAL) {
    newDiscountThreshold = _.get(formValues, COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD)
    newDiscountAmount = _.get(formValues, COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT)
  }
  const targetAudience = _.get(formValues, COUPON_FIELD.TARGET_AUDIENCE)
  // api 只吃 abandonedCart，前端用 abandonedCart、abandonedCartQuantity 區分 icon 顯示狀態
  // 所以送出前要改回來
  const convertedTargetAudience = (targetAudience === TARGET_AUDIENCE.ABANDONED_CART_QUANTITY)
    ? TARGET_AUDIENCE.ABANDONED_CART
    : targetAudience
  const newFormValues = {
    ...formValues,
    targetAudience: convertedTargetAudience,
    listingIds: [formValues.listingId],
    title: `購物車專屬折價券-${formValues[COUPON_FIELD.TITLE]}`,
    discountThreshold: newDiscountThreshold,
    discountAmount: newDiscountAmount
  }

  const requiredFields = [
    'listingIds',
    'title',
    'targetAudience',
    'discountAmount',
    'discountThreshold',
    'discountCount',
    'discountRule',
    'duration',
    'isAllowedActivity',
    'imageUrl'
  ]
  const createdPayload = _.pick(newFormValues, requiredFields)
  return createdPayload
}

const withSharedModalsActions = (WrappedComponent) => {
  const WrappedComponentWithModals = (props) => {
    // 查看更多頁需從 props.actions 取得
    const { createSupplierCoupons, fetchProductBundles } = props.actions || props
    const [eCouponVisible, setECouponVisible] = useState(false)
    const [listingsInfoData, setListingsInfoData] = useState({
      listing: {},
      promotion: {},
      targetAudience: null
    })
    const [resType, setResType] = useState(RES_TYPE.SUCCESS)
    const [successData, setSuccessData] = useState({})
    const [formVisible, setFormVisible] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [couponsError, setCouponsError] = useState({})

    const onCloseECouponModal = () => {
      setECouponVisible(false)
      setFormVisible(true)
    }

    const submitCouponModal = async (formValues, syncErrorsStatus) => {
      const createdPayload = getConvertedCreatedPayload(formValues)
      // 欄位驗證有錯誤
      if (syncErrorsStatus) {
        return
      }

      // POST /product/v1/supplierCoupons
      setIsLoading(true)
      const req = params => createSupplierCoupons(params).then(httpHandler)
      const [newCouponsError, coupons = {}] = await createSupplierCouponsData(createdPayload, req)
      setCouponsError(newCouponsError)
      if (newCouponsError) {
        setResType(RES_TYPE.ERROR)
        console.error(newCouponsError)
      } else if (coupons.discountCount === 0) {
        setResType(RES_TYPE.DISCOUNT_COUNT_ZERO)
      } else {
        setResType(RES_TYPE.SUCCESS)
        setSuccessData(coupons)
      }
      setIsLoading(false)
      setFormVisible(false)
    }

    const onOpenECouponModal = async (listingId, targetAudience, data) => {
      const keyByListingsData = _.keyBy(data, 'listingId')
      const listingData = _.get(keyByListingsData, listingId, {})
      let isShowIcon = false
      switch (targetAudience) {
        case TARGET_AUDIENCE.YESTERDAY_ABANDONED_CART: {
          isShowIcon = listingData.showYesterdayAbandonedCartIcon
          break
        }
        case TARGET_AUDIENCE.ABANDONED_CART: {
          isShowIcon = listingData.showAbandonedCartIcon
          break
        }
        case TARGET_AUDIENCE.ABANDONED_CART_QUANTITY: {
          isShowIcon = listingData.showAbandonedCartQuantityIcon
          break
        }
        default: {
          break
        }
      }
      if (!isShowIcon) {
        Toast.error('此賣場無法發送折價券，請選擇其他賣場。')
        return
      }
      const req = params => fetchProductBundles(params).then(httpHandler)
      const [promotionError, promotionData = {}] = await getProductBundlesData(listingId, req)
      if (promotionError) {
        Toast.error(`取得活動資料失敗：${promotionError.message}`)
        console.error(promotionError)
      }

      // 將賣場資訊及活動資訊資料合併
      setListingsInfoData({ listing: listingData, promotion: promotionData, targetAudience })
    }

    return (
      <>
        <WrappedComponent
          {...props}
          onOpenECouponModal={onOpenECouponModal}
          setECouponVisible={setECouponVisible}
        />
        {
          eCouponVisible && (
            <ECouponModal
              listingsInfoData={listingsInfoData}
              successData={successData}
              eCouponVisible={eCouponVisible}
              formVisible={formVisible}
              resType={resType}
              isLoading={isLoading}
              couponsError={couponsError}
              onCloseModal={onCloseECouponModal}
              onSubmitModal={submitCouponModal}
            />
          )
        }
      </>
    )
  }

  return WrappedComponentWithModals
}

export default withSharedModalsActions
